import { useCallback, useEffect, useRef, useState } from 'react'
import { TESTIMONIALS } from './testimonialsData'

const AUTOPLAY_MS = 6000

function getVisible() {
  if (typeof window === 'undefined') return 3
  if (window.innerWidth >= 1024) return 3
  if (window.innerWidth >= 768) return 2
  return 1
}

export default function TestimonialsSection() {
  const [visible, setVisible] = useState(getVisible)
  const [current, setCurrent] = useState(0)
  const timerRef = useRef(null)
  const pausedRef = useRef(false)

  const total = TESTIMONIALS.length
  const maxIdx = Math.max(0, total - visible)

  useEffect(() => {
    const onResize = () => setVisible(getVisible())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    setCurrent((c) => Math.min(c, maxIdx))
  }, [maxIdx])

  const stop = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const start = useCallback(() => {
    stop()
    if (pausedRef.current) return
    timerRef.current = window.setInterval(() => {
      setCurrent((c) => (c >= maxIdx ? 0 : c + 1))
    }, AUTOPLAY_MS)
  }, [stop, maxIdx])

  useEffect(() => {
    start()
    return stop
  }, [start, stop])

  const goTo = (idx) => setCurrent(Math.max(0, Math.min(maxIdx, idx)))

  return (
    <section className="relative overflow-hidden py-16 md:py-24 md:px-14 hero-vedic text-white font-normal">
      <div className="container relative z-10 mx-auto px-6 max-w-[1400px]">
        <div className="mb-10 flex flex-col items-center text-center md:mb-12">
          <div className="flex items-center gap-3">
            <span className="h-px w-8 bg-secondary/50" />
            <span className="text-[10px] uppercase tracking-[0.4em] text-secondary">Seeker Experiences</span>
            <span className="h-px w-8 bg-secondary/50" />
          </div>
          <h2 className="mt-3 text-3xl leading-tight text-[#FBF3E4] md:text-4xl font-cinzel">
            Authenticated <span className="text-gradient-gold">Testimonials</span>
          </h2>
        </div>

        <div
          className="overflow-hidden"
          onMouseEnter={() => { pausedRef.current = true; stop() }}
          onMouseLeave={() => { pausedRef.current = false; start() }}
        >
          <div
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${(100 / visible) * current}%)` }}
          >
            {TESTIMONIALS.map((item) => (
              <div key={item.id} className="shrink-0 px-2.5" style={{ width: `${100 / visible}%` }}>
                <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-sm transition-all duration-400 hover:-translate-y-1 hover:border-secondary/40 h-full">
                  <p className="italic text-[13px] leading-relaxed text-white/65">&quot;{item.quote}&quot;</p>
                  <div className="mt-5 flex items-center gap-3 border-t border-white/10 pt-4">
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-secondary/15 text-secondary ring-1 ring-secondary/25 font-bold">
                      {item.initial}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="truncate text-[13px] text-[#FBF3E4]">{item.name}</h4>
                      <p className="truncate text-[9px] tracking-[0.18em] text-white/40">{item.loc}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="swiper-pagination mt-8 flex items-center justify-center gap-2">
          {Array.from({ length: maxIdx + 1 }, (_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to testimonial page ${i + 1}`}
              onClick={() => { goTo(i); stop(); start() }}
              className="rounded-full transition-all duration-300"
              style={{
                width: i === current ? 22 : 6,
                height: 6,
                background: i === current ? '#D4AF37' : 'rgba(255,255,255,.25)',
              }}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
